/**
 * Shared in-memory cache for parsed maneuver timeseries payloads.
 * Used by maneuver video Maneuver Text overlays to avoid duplicate API calls per tile/channel.
 */

import {
  fetchManeuverTimeseriesValues,
} from "../services/maneuversDataService";
import {
  parseManeuverTimeseriesValuesResponse,
  type ManeuverTsPoint,
  type ParseManeuverTimeseriesValuesResult,
} from "../utils/maneuverTimeseriesGroups";
import {
  parseManeuverTimeseriesValuesOffThread,
  countManeuverValueSamples,
  MANEUVER_PARSE_OFFLOAD_MIN_SAMPLES,
} from "../utils/maneuverTimeseriesParseTask";
import type { ManeuversContext } from "../utils/maneuversConfig";
import { debug, error as logError } from "../utils/console";

export {
  MANEUVER_TEXT_OVERLAY_TYPE,
  MANEUVER_VIDEO_OVERLAY_OBJECT_NAME,
} from "../utils/maneuverVideoOverlayConfig";

const PARSED_CACHE_TTL_MS = 15 * 60 * 1000;

export interface ManeuverTimeseriesCacheKey {
  context: ManeuversContext;
  phase: string;
  eventType: string;
  eventIds: number[];
  channels: string[];
}

export interface ManeuverTimeseriesCacheEntry extends ParseManeuverTimeseriesValuesResult {
  eventIdToIndex: Map<number, number>;
  status: "loading" | "loaded" | "error";
}

interface ParsedCacheRecord {
  entry: ManeuverTimeseriesCacheEntry;
  expiresAt: number;
}

const parsedCache = new Map<string, ParsedCacheRecord>();
const inflight = new Map<string, Promise<ManeuverTimeseriesCacheEntry>>();

function sortedEventIds(eventIds: number[]): number[] {
  return [...new Set(eventIds.filter((id) => Number.isFinite(id)))].sort((a, b) => a - b);
}

function sortedChannels(channels: string[]): string[] {
  return [...new Set(channels.map((c) => c.toLowerCase()).filter(Boolean))].sort();
}

/** Cache key includes phase AND sorted lowercased channels. */
export function buildManeuverTimeseriesCacheKey(input: ManeuverTimeseriesCacheKey): string {
  const ids = sortedEventIds(input.eventIds).join(",");
  const ch = sortedChannels(input.channels).join(",");
  return `${input.context}|${input.eventType}|${input.phase}|${ch}|${ids}`;
}

function buildEventIdIndex(dataArray: ManeuverTsPoint[][]): Map<number, number> {
  const map = new Map<number, number>();
  dataArray.forEach((series, index) => {
    const eventId = series[0]?.event_id;
    if (typeof eventId === "number") {
      map.set(eventId, index);
    }
  });
  return map;
}

export function getOrderedManeuverChannelNames(entry: ManeuverTimeseriesCacheEntry | undefined): string[] {
  if (!entry) {return [];}
  if (entry.channelOrder.length > 0) {return [...entry.channelOrder];}
  const firstSeries = entry.dataArray[0];
  const firstPoint = firstSeries?.[0];
  if (!firstPoint) {return [];}
  return Object.keys(firstPoint).filter(
    (key) => !["event_id", "time", "tack", "race", "source_name", "tws_bin", "vmg_perc_avg", "State", "Config"].includes(key)
  );
}

function getFreshCachedEntry(key: string): ManeuverTimeseriesCacheEntry | undefined {
  const record = parsedCache.get(key);
  if (!record) {return undefined;}
  if (Date.now() > record.expiresAt) {
    parsedCache.delete(key);
    return undefined;
  }
  return record.entry.status === "loaded" ? record.entry : undefined;
}

export function getCachedManeuverTimeseries(key: string): ManeuverTimeseriesCacheEntry | undefined {
  return getFreshCachedEntry(key);
}

export function primeManeuverTimeseriesCache(
  keyInput: ManeuverTimeseriesCacheKey,
  parsed: ParseManeuverTimeseriesValuesResult
): ManeuverTimeseriesCacheEntry {
  const key = buildManeuverTimeseriesCacheKey(keyInput);
  const entry: ManeuverTimeseriesCacheEntry = {
    ...parsed,
    eventIdToIndex: buildEventIdIndex(parsed.dataArray),
    status: "loaded",
  };
  parsedCache.set(key, { entry, expiresAt: Date.now() + PARSED_CACHE_TTL_MS });
  return entry;
}

export interface FetchManeuverTimeseriesCacheParams extends ManeuverTimeseriesCacheKey {
  signal?: AbortSignal;
}

export async function fetchManeuverTimeseriesCache(
  params: FetchManeuverTimeseriesCacheParams
): Promise<ManeuverTimeseriesCacheEntry> {
  const eventIds = sortedEventIds(params.eventIds);
  const channels = sortedChannels(params.channels);
  if (eventIds.length === 0 || channels.length === 0) {
    return { dataArray: [], charts: [], channelOrder: [], eventIdToIndex: new Map(), status: "loaded" };
  }

  const key = buildManeuverTimeseriesCacheKey({ ...params, eventIds, channels });
  const cached = getFreshCachedEntry(key);
  if (cached) {return cached;}

  const pending = inflight.get(key);
  if (pending) {return pending;}

  const promise = (async (): Promise<ManeuverTimeseriesCacheEntry> => {
    const loadingEntry: ManeuverTimeseriesCacheEntry = {
      dataArray: [],
      charts: [],
      channelOrder: [],
      eventIdToIndex: new Map(),
      status: "loading",
    };
    parsedCache.set(key, { entry: loadingEntry, expiresAt: Date.now() + PARSED_CACHE_TTL_MS });

    try {
      const rows = await fetchManeuverTimeseriesValues({
        eventType: params.eventType,
        phase: params.phase,
        eventList: eventIds,
        channels,
        signal: params.signal,
      });

      if (params.signal?.aborted) {
        return { dataArray: [], charts: [], channelOrder: [], eventIdToIndex: new Map(), status: "error" };
      }

      // Large parses (O(events × samples × channels)) run across a @nodets worker pool to keep the
      // load off the main thread; small ones stay inline and never pay worker spawn cost. Output is
      // identical either way (see maneuverTimeseriesParseTask.ts + its parity test).
      const parsed =
        countManeuverValueSamples(rows) >= MANEUVER_PARSE_OFFLOAD_MIN_SAMPLES
          ? await parseManeuverTimeseriesValuesOffThread(rows, eventIds, channels)
          : parseManeuverTimeseriesValuesResponse(rows, eventIds, channels);
      const entry: ManeuverTimeseriesCacheEntry = {
        ...parsed,
        eventIdToIndex: buildEventIdIndex(parsed.dataArray),
        status: "loaded",
      };
      parsedCache.set(key, { entry, expiresAt: Date.now() + PARSED_CACHE_TTL_MS });
      debug("maneuverTimeseriesCache: loaded", {
        key,
        events: eventIds.length,
        channels: getOrderedManeuverChannelNames(entry).length,
      });
      return entry;
    } catch (err: unknown) {
      const name = err && typeof err === "object" && "name" in err ? String((err as { name?: string }).name) : "";
      if (name !== "AbortError") {
        logError("maneuverTimeseriesCache: fetch failed", err);
      }
      const failed: ManeuverTimeseriesCacheEntry = {
        dataArray: [],
        charts: [],
        channelOrder: [],
        eventIdToIndex: new Map(),
        status: "error",
      };
      parsedCache.set(key, { entry: failed, expiresAt: Date.now() + PARSED_CACHE_TTL_MS });
      return failed;
    } finally {
      inflight.delete(key);
    }
  })();

  inflight.set(key, promise);
  return promise;
}

function getChannelValue(point: ManeuverTsPoint, channelName: string): number | null {
  if (!point || !channelName) {return null;}
  const variations = [
    channelName,
    channelName.toLowerCase(),
    channelName.toUpperCase(),
    channelName.charAt(0).toUpperCase() + channelName.slice(1).toLowerCase(),
  ];
  for (const variant of variations) {
    const raw = point[variant];
    if (raw !== undefined && raw !== null) {
      const value = Number(raw);
      if (!Number.isNaN(value) && Number.isFinite(value)) {return value;}
    }
  }
  const match = Object.keys(point).find(
    (key) =>
      key.toLowerCase() === channelName.toLowerCase() ||
      key.toLowerCase().replace(/_/g, "") === channelName.toLowerCase().replace(/_/g, "")
  );
  if (match) {
    const value = Number(point[match]);
    if (!Number.isNaN(value) && Number.isFinite(value)) {return value;}
  }
  return null;
}

/** Nearest sample at maneuver-relative seconds for one event. */
export function getManeuverChannelValueAtTime(
  entry: ManeuverTimeseriesCacheEntry | undefined,
  eventId: number,
  channelName: string,
  relativeTimeSec: number
): number | null {
  if (!entry || entry.status !== "loaded") {return null;}
  const seriesIndex = entry.eventIdToIndex.get(eventId);
  if (seriesIndex === undefined) {return null;}
  const series = entry.dataArray[seriesIndex];
  if (!series?.length) {return null;}

  let closest: ManeuverTsPoint | null = null;
  let minDiff = Infinity;
  for (const point of series) {
    const t = Number(point.time);
    if (!Number.isFinite(t)) {continue;}
    const diff = Math.abs(t - relativeTimeSec);
    if (diff < minDiff) {
      minDiff = diff;
      closest = point;
    }
  }
  if (!closest) {return null;}
  return getChannelValue(closest, channelName);
}
